
import { useState } from "react";
import { useAccount, usePublicClient, useWalletClient } from "wagmi";
import { parseEther, decodeEventLog, encodeFunctionData, isAddress, zeroAddress, type Address } from "viem";
import { useToast } from "@/hooks/use-toast";
import { DAO_CONTRACTS, DAO_FACTORY_ADDRESS, type DAOConfig } from "../contracts/config";
import { useNetworkSwitch } from "@/components/create-coin/form/hooks/useNetworkSwitch";
import { polygon } from "viem/chains";
import { supabase } from "@/integrations/supabase/client";
import { useLaunchTrustRegistration } from "@/features/identity/useLaunchTrustRegistration";

interface DAOFormData {
  name?: string;
  symbol?: string;
  initialSupply?: string;
  description?: string;
  chain?: string;
  acceptedTokens?: string;
  deployerVeto?: boolean;
  governorUpgradeable?: boolean;
  multichain?: boolean;
  additionalChains?: string[];
  proposalThreshold?: string;
  quorumPercentage?: string;
  votingDelay?: string;
  votingPeriod?: string;
  daoCategories?: string[];
  trustEnabled?: boolean;
  founderEntityId?: string;
  entityMetadataURI?: string;
  projectMetadataURI?: string;
}

export function useDAODeployment() {
  const { toast } = useToast();
  const { address, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient({ chainId: polygon.id });
  const { ensureCorrectNetwork } = useNetworkSwitch();
  const [isDeploying, setIsDeploying] = useState(false);
  const trustRegistration = useLaunchTrustRegistration();

  const deployDAO = async (formData: DAOFormData) => {
    if (!isConnected || !address || !walletClient) {
      toast({
        title: "Wallet Not Connected",
        description: "Please connect your wallet to create a DAO",
        variant: "destructive",
      });
      return;
    }

    // Validate required fields
    if (!formData.name || !formData.symbol) {
      toast({
        title: "Missing Required Fields",
        description: "Please provide a name and symbol for your DAO",
        variant: "destructive",
      });
      return;
    }

    // Check if contracts are compiled
    if (!DAO_CONTRACTS.DAOFactory.abi || !Array.isArray(DAO_CONTRACTS.DAOFactory.abi)) {
      toast({
        title: "Contracts Not Ready",
        description: "DAO contracts need to be compiled first. Please run the compilation script.",
        variant: "destructive",
      });
      return;
    }

    if (!DAO_FACTORY_ADDRESS) {
      toast({
        title: "Factory Not Available",
        description: "DAO Factory contract is not deployed. Please contact support.",
        variant: "destructive",
      });
      return;
    }

    // Ensure correct network
    const networkReady = await ensureCorrectNetwork();
    if (!networkReady) return;

    // The wallet client captured before the network switch can still report
    // Ethereum. Switch through that client as well and verify the provider
    // before preparing the transaction.
    const initialWalletChainId = await walletClient.getChainId();
    if (initialWalletChainId !== polygon.id) {
      try {
        await walletClient.switchChain({ id: polygon.id });
      } catch (error) {
        toast({
          title: "Switch to Polygon Required",
          description: "Please approve the network change in your wallet, then try again.",
          variant: "destructive",
        });
        return;
      }
    }
    const walletChainId = await walletClient.getChainId();
    if (walletChainId !== polygon.id) {
      toast({
        title: "Wrong Network",
        description: `Your wallet is still on chain ${walletChainId}. Switch to Polygon (137) before creating the DAO.`,
        variant: "destructive",
      });
      return;
    }

    setIsDeploying(true);

    try {
      const trustProject = await trustRegistration.registerBeforeLaunch({
        enabled: formData.trustEnabled,
        entityId: formData.founderEntityId,
        entityType: 3,
        entityMetadataURI: formData.entityMetadataURI,
        projectMetadataURI: formData.projectMetadataURI,
        projectKind: 2,
      });

      // Convert form data to contract config
      const daoConfig: DAOConfig = {
        name: formData.name,
        symbol: formData.symbol,
        initialSupply: parseEther(formData.initialSupply || "1000000"),
        votingDelay: BigInt(formData.votingDelay || "1"),
        votingPeriod: BigInt(formData.votingPeriod || "5760"),
        proposalThreshold: parseEther(formData.proposalThreshold || "1000"),
        quorumPercentage: BigInt(formData.quorumPercentage || "4"),
        timelockDelay: BigInt(86400), // 1 day default
        deployerVeto: formData.deployerVeto || false,
        governorUpgradeable: formData.governorUpgradeable || false,
      };

      toast({
        title: "Validating DAO deployment",
        description: "Checking the Polygon factory configuration before opening your wallet...",
      });

      if (!publicClient) {
        throw new Error("Polygon RPC is not available. Please try again in a moment.");
      }

      // Fail before opening the wallet when the live factory is stale or only
      // partially configured. Nested CREATE failures otherwise lose their
      // revert reason on some Polygon RPC providers.
      const templateFunctions = [
        "tokenTemplate",
        "treasuryTemplate",
        daoConfig.governorUpgradeable ? "upgradeableGovernorTemplate" : "governorTemplate",
      ] as const;
      let templateAddresses: Address[];
      try {
        templateAddresses = await Promise.all(templateFunctions.map(async (functionName) => {
          const template = await publicClient.readContract({
            address: DAO_FACTORY_ADDRESS as Address,
            abi: DAO_CONTRACTS.DAOFactory.abi,
            functionName,
          } as never) as Address;
          return template;
        }));
      } catch (error) {
        console.error("DAO factory compatibility check failed:", error);
        throw new Error(`The configured Polygon factory (${DAO_FACTORY_ADDRESS}) is incompatible with this app version. Deploy and configure the current DAOFactorySplit stack, then update VITE_DAO_FACTORY_ADDRESS.`);
      }

      const invalidTemplateIndex = (await Promise.all(templateAddresses.map(async (template) => (
        !isAddress(template) || template === zeroAddress || !(await publicClient.getBytecode({ address: template }))
      )))).findIndex(Boolean);
      if (invalidTemplateIndex !== -1) {
        throw new Error(`The DAO factory's ${templateFunctions[invalidTemplateIndex]} is not configured with a deployed contract.`);
      }

      // Simulate with enough gas for the nested token, treasury, and governor
      // deployments. The current stack consumes about 3.8M gas locally; an
      // explicit limit avoids low eth_estimateGas caps in wallet RPCs.
      const deploymentGasLimit = 5_000_000n;
      const callData = encodeFunctionData({
        abi: DAO_CONTRACTS.DAOFactory.abi,
        functionName: "createDAO",
        args: [daoConfig],
      } as never);
      await publicClient.call({
        account: address,
        to: DAO_FACTORY_ADDRESS as Address,
        data: callData,
        gas: deploymentGasLimit,
      });

      toast({
        title: "Deploying DAO",
        description: "Validation passed. Please confirm the transaction in your wallet...",
      });

      // Call the DAOFactory contract
      const hash = await walletClient.writeContract({
        address: DAO_FACTORY_ADDRESS as `0x${string}`,
        abi: DAO_CONTRACTS.DAOFactory.abi,
        functionName: 'createDAO',
        args: [daoConfig],
        account: address,
        chain: polygon,
        gas: deploymentGasLimit,
      } as never) as `0x${string}`;

      toast({
        title: "Transaction Sent",
        description: "Your DAO is being created. This may take a few minutes...",
      });

      // Wait for transaction confirmation
      const receipt = await publicClient.waitForTransactionReceipt({ 
        hash,
        confirmations: 2,
      });

      // Parse the DAOCreated event to get addresses
      let tokenAddress: string | null = null;
      let governorAddress: string | null = null;
      let treasuryAddress: string | null = null;
      
      console.log("Transaction receipt logs:", receipt.logs);
      
      if (receipt.logs && receipt.logs.length > 0) {
        try {
          for (const log of receipt.logs) {
            try {
              // Try to decode the log
              const topics = (log as any).topics as `0x${string}`[];
              if (!topics) continue;
              
              const decodedLog = decodeEventLog({
                abi: DAO_CONTRACTS.DAOFactory.abi,
                data: log.data,
                topics: [...topics] as [`0x${string}`, ...`0x${string}`[]],
              }) as any;
              
              console.log("Decoded log:", decodedLog);
              
              if (decodedLog.eventName === 'DAOCreated') {
                const args = decodedLog.args as any;
                const values = Object.values(args || {}).filter((value) => typeof value === "string" && value.startsWith("0x"));
                tokenAddress = args.token || args.tokenAddress || values[1] || values[0] || null;
                governorAddress = args.governor || args.governorAddress || values[2] || values[1] || null;
                treasuryAddress = args.treasury || args.treasuryAddress || values[3] || values[2] || null;
                console.log("Found DAO addresses:", { tokenAddress, governorAddress, treasuryAddress });
                break;
              }
            } catch (e) {
              // Some deployed factory versions have the same event layout but
              // a different ABI artifact. Decode the indexed addresses and
              // first non-indexed address directly as a compatibility fallback.
              const topics = (log as any).topics as `0x${string}`[];
              const data = (log as any).data as `0x${string}`;
              if (topics?.length >= 4 && data?.length >= 66) {
                tokenAddress = `0x${topics[2].slice(-40)}`;
                governorAddress = `0x${topics[3].slice(-40)}`;
                treasuryAddress = `0x${data.slice(2, 66).slice(-40)}`;
                console.log("Found DAO addresses via compatibility decoder:", { tokenAddress, governorAddress, treasuryAddress });
                break;
              }
            }
          }
        } catch (error) {
          console.error("Error parsing DAO event:", error);
        }
      }
      
      if (!tokenAddress || !governorAddress || !treasuryAddress) {
        console.warn("Failed to parse DAO addresses from event logs");
        toast({
          title: "Warning",
          description: "DAO may have been created but addresses could not be parsed. Check the transaction on a block explorer.",
          variant: "destructive",
        });
      }

      if (trustProject && governorAddress) {
        await trustRegistration.bindLaunchAsset(trustProject.projectId, governorAddress as Address);
      }

      // Save DAO to database (no authentication required)
      if (tokenAddress && governorAddress && treasuryAddress) {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          
          const daoRecord = {
            name: daoConfig.name,
            symbol: daoConfig.symbol,
            creator_address: address.toLowerCase(),
            deployer_user_id: user?.id || null,
            token_address: tokenAddress.toLowerCase(),
            governor_address: governorAddress.toLowerCase(),
            treasury_address: treasuryAddress.toLowerCase(),
            initial_supply: Number(daoConfig.initialSupply / BigInt(1e18)),
            voting_delay: Number(daoConfig.votingDelay),
            voting_period: Number(daoConfig.votingPeriod),
            proposal_threshold: Number(daoConfig.proposalThreshold / BigInt(1e18)),
            quorum_percentage: Number(daoConfig.quorumPercentage),
            timelock_delay: Number(daoConfig.timelockDelay),
            deployer_veto: daoConfig.deployerVeto,
          };
          
          console.log("Saving DAO to database:", daoRecord);
          
          const { data: savedDao, error: dbError } = await supabase
            .from("daos")
            .insert([daoRecord])
            .select();

          if (dbError) {
            console.error("Database error details:", dbError);
            toast({
              title: "Warning",
              description: `DAO deployed but save failed: ${dbError.message}`,
              variant: "destructive",
            });
          } else {
            console.log("DAO saved successfully to database:", savedDao);
          }
        } catch (error) {
          console.error("Database error:", error);
        }
      }

      toast({
        title: "DAO Created Successfully!",
        description: `Your DAO has been deployed. Transaction: ${hash}`,
      });

      return {
        success: true,
        transactionHash: hash,
        daoAddress: governorAddress || address, // Use governor address as primary DAO address
        tokenAddress,
        governorAddress,
        treasuryAddress,
      };

    } catch (error: any) {
      console.error("DAO deployment error:", error);
      
      let errorMessage = "Unknown error occurred";
      if (error.message) {
        if (error.message.includes("insufficient funds")) {
          errorMessage = "You don't have enough MATIC to deploy the DAO";
        } else if (error.message.includes("user rejected")) {
          errorMessage = "You rejected the transaction";
        } else {
          errorMessage = error.message;
        }
      }

      toast({
        title: "DAO Creation Failed",
        description: errorMessage,
        variant: "destructive",
      });

      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsDeploying(false);
    }
  };

  return {
    deployDAO,
    isDeploying,
  };
}
